/**
 * Référentiel fit-culturel d'une campagne, affiché comme la passation d'un
 * candidat (`FitCulturelResults`) : même pyramide, mêmes niveaux, mêmes quotas,
 * mêmes teintes.
 *
 * La STRUCTURE est identique à celle du candidat, et c'est le point : les deux
 * pyramides se superposent, donc l'écart se lit sans retraduction mentale. Les
 * items ne sont pas placés par arrondi de la moyenne (qui produisait une pyramide
 * bosselée, incomparable à celle du candidat) mais RECLASSÉS par score référents
 * dans les capacités de la pyramide — `niveauxForces`, calculé une fois par
 * version côté Convex et partagé avec le score de matching et le rapport.
 *
 * Le prix du reclassement : deux items de moyennes proches peuvent se retrouver à
 * deux niveaux différents. D'où la moyenne et l'écart-type portés PAR ITEM plutôt
 * que résumés en bas de page — c'est item par item qu'on décide si un placement
 * est une vraie attente ou un désaccord entre référents.
 */
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { libellesNiveauxQsort } from "@repo/utils";
import { QsortPyramide, type ItemPyramide } from "./QsortPyramide";

type Dimension = { code: string; label: string; description?: string };

/** Agrégat Q-sort d'une dimension (`moyenneQsort`) : moyenne des scores 1..N. */
type StatDimension = { moyenne: number; ecartType: number; n: number };

type Props = {
  dimensions: Dimension[];
  agregat: Record<string, StatDimension>;
  /** Capacités de la pyramide de la campagne (`2-3-4-5-4-3-2`, ou `2-3-5-3-2`
   *  pour une campagne configurée avant le figement à 23 items). */
  pyramide: number[];
  /** `code` → niveau du reclassement forcé (0 = le plus fort). Fait foi : c'est
   *  la structure que le score de matching et le rapport utilisent aussi. */
  niveauxForces: Record<string, number>;
  /** Codes signalés en désaccord par l'agrégation, cerclés dans la pyramide. */
  codesDesaccord?: string[];
  /** Identité de la version — doit accompagner toute lecture du référentiel. */
  version?: { version: number; nbReferents: number; fige: boolean };
};

/** Un écart-type d'au moins un cran complet : les référents ne sont pas d'accord
 *  sur le niveau de l'item, la moyenne est à lire avec précaution. */
const SEUIL_DISPERSION = 1;

export function ReferentielPyramide({
  dimensions,
  agregat,
  pyramide,
  niveauxForces,
  codesDesaccord = [],
  version,
}: Props) {
  const niveaux = pyramide.length;
  const libelles = libellesNiveauxQsort(niveaux);
  const desaccord = new Set(codesDesaccord);

  // Un item est « renseigné » s'il a un score référents ET une place dans le
  // reclassement — les deux vont de pair, le reclassement ne classe que ce que
  // l'agrégat renseigne.
  const renseignees = dimensions.filter(
    (d) => agregat[d.code] !== undefined && niveauxForces[d.code] !== undefined,
  );
  const absentes = dimensions.filter(
    (d) => agregat[d.code] === undefined || niveauxForces[d.code] === undefined,
  );

  const items: ItemPyramide[] = renseignees.map((d) => {
    const stat = agregat[d.code]!;
    const disperse = stat.ecartType >= SEUIL_DISPERSION;
    return {
      code: d.code,
      label: d.label,
      niveau: niveauxForces[d.code]!,
      valeur: stat.moyenne.toFixed(1),
      detail: `± ${stat.ecartType.toFixed(1)} · ${stat.n} référent${stat.n > 1 ? "s" : ""}`,
      alerte: disperse || desaccord.has(d.code),
    };
  });

  const nbAlertes = items.filter((i) => i.alerte).length;

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-3">
          Pyramide du référentiel
          {version && (
            <>
              <Badge variant="info">
                v{version.version} · {version.nbReferents} référent
                {version.nbReferents > 1 ? "s" : ""}
              </Badge>
              <Badge variant={version.fige ? "purple" : "outline"}>
                {version.fige ? "Figé" : "Ouvert"}
              </Badge>
            </>
          )}
        </CardTitle>
        <CardDescription>
          Attentes des référents, du plus essentiel (en haut) au sans importance
          (en bas). Même structure que la pyramide d'un candidat — mêmes niveaux,
          mêmes quotas — pour que les deux se superposent&nbsp;: les items sont
          classés par score moyen des référents, puis versés dans les capacités de
          la pyramide. Chaque item porte sa moyenne et sa dispersion entre
          référents.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <QsortPyramide
          items={items}
          niveaux={niveaux}
          pyramide={pyramide}
          videMessage="Aucun référent n'a encore contribué : le référentiel est vide."
        />

        {legendeDispersion(nbAlertes)}

        {absentes.length > 0 && (
          <div className="text-sm">
            <p className="font-medium">
              Items non renseignés ({absentes.length})
            </p>
            <p className="text-muted-foreground">
              Aucun référent ne les a classés : {absentes.map((d) => d.label).join(", ")}.
            </p>
          </div>
        )}

        <details className="text-sm">
          <summary className="cursor-pointer font-medium">
            Détail par item
          </summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Item</th>
                  <th className="py-2 pr-4 font-medium">Niveau</th>
                  <th className="py-2 pr-4 font-medium">Moyenne</th>
                  <th className="py-2 pr-4 font-medium">Dispersion</th>
                  <th className="py-2 font-medium">Référents</th>
                </tr>
              </thead>
              <tbody>
                {renseignees
                  .map((d) => ({
                    d,
                    stat: agregat[d.code]!,
                    niveau: niveauxForces[d.code]!,
                  }))
                  // Ordre du reclassement : niveau, puis départage à l'identique
                  // (dispersion croissante), pour que le tableau donne la même
                  // séquence que la pyramide au-dessus.
                  .sort(
                    (a, b) =>
                      a.niveau - b.niveau ||
                      b.stat.moyenne - a.stat.moyenne ||
                      a.stat.ecartType - b.stat.ecartType,
                  )
                  .map(({ d, stat, niveau }) => (
                    <tr key={d.code} className="border-b last:border-0">
                      <td className="py-2 pr-4">{d.label}</td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {libelles[niveau]}
                      </td>
                      <td className="py-2 pr-4 tabular-nums">
                        {stat.moyenne.toFixed(1)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums">
                        ± {stat.ecartType.toFixed(1)}
                      </td>
                      <td className="py-2 tabular-nums">{stat.n}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

/** Légende de la mise en garde, rendue seulement s'il y a matière. */
function legendeDispersion(nb: number) {
  if (nb === 0) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {nb} item{nb > 1 ? "s" : ""} cerné{nb > 1 ? "s" : ""} en jaune : les
      référents divergent d'au moins un cran, la moyenne ne représente aucun
      d'eux exactement.
    </p>
  );
}
